import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injectable,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    enumMapper,
    EnumSortDirection,
    IRobot,
    ODataResponse,
    RobotHostingType,
    RobotType,
} from '@models';
import {
    GRID_SERVICE,
    IGridSource,
    ODataGridComponent,
} from '@pages/generics/grid';
import {
    EnvironmentService,
    ODataQueryOptions,
    RobotService,
} from '@services';

import { Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Injectable()
export class RobotEnvironmentSource implements IGridSource<ODataQueryOptions, ODataResponse<IRobot>> {
    public environmentId!: number;

    public get mapper() {
        return this._robots.mapper;
    }

    constructor(
        private _robots: RobotService,
        private _environments: EnvironmentService,
    ) { }

    public get(qo: ODataQueryOptions) {
        return this._environments.getRobotsIn(this.environmentId, qo);
    }
}

@Component({
    selector: 'ui-environment-robots-grid',
    templateUrl: './environment-robots.grid.html',
    providers: [
        EnvironmentService,
        RobotEnvironmentSource, {
            provide: GRID_SERVICE,
            useClass: RobotEnvironmentSource,
        }],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnvironmentRobotsGridComponent
    extends ODataGridComponent<IRobot, number>
    implements OnInit, OnDestroy {

    @Input()
    public set environmentId(value: number) {
        const svc = (this._service as unknown as RobotEnvironmentSource);
        if (
            !svc ||
            isNaN(value) ||
            svc.environmentId === value
        ) { return; }

        svc.environmentId = value;
        this._environmentId = value;
        this._refresh$.next();
    }

    public RobotHostingType = RobotHostingType;
    public RobotType = RobotType;

    @Output()
    public close = new EventEmitter<void>();

    public isInConflictingState$ = new Subject<boolean>();

    public typeList = enumMapper(RobotType, {
        resourceKey: 'RobotType',
        sort: EnumSortDirection.ascending,
    });

    public hostingTypeList = enumMapper(RobotHostingType, {
        resourceKey: 'RobotHostingType',
    });

    private _environmentId!: number;
    private _environmentService = this._injector.get<EnvironmentService>(EnvironmentService);

    ngOnInit() {
        this._environmentService
            .getRobotIdsForEnvironment(this._environmentId)
            .subscribe(robotIds => {
                const robots = robotIds as IRobot[];
                this._grid.selectionManager.select(...robots);
                this._grid.selectionManager.snapshot();
            });
    }

    ngOnDestroy() {
        super.ngOnDestroy();

        this.close.complete();
    }

    public updateEnvironment() {
        this.loading$.next(true);

        this._environmentService.setRobots(
            this._environmentId,
            this._grid.selectionManager.difference,
        ).pipe(
            finalize(
                () => this.loading$.next(false),
            ),
        )
            .subscribe(
                () => { this.close.emit(); },
                (errorResponse) => {
                    if (errorResponse.status === 409) {
                        this.isInConflictingState$.next(true);

                        const failedItems: IRobot[] = errorResponse
                            .error
                            .resourceIds
                            .map((id: number) => ({ id }));

                        this._grid.selectionManager.deselect(...failedItems);

                        this._refresh$.next();
                    }
                });
    }
}
